import { useCallback, useMemo, useState, useRef } from 'react';

import moment from 'moment';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

import { forceDateString } from 'src/utils/datetime';
import ConfirmModal from 'src/components/ConfirmModal';
import { DatePickerField } from 'src/components/DatePickerField';

import type { Moment } from 'moment';

import messages from './messages';

interface DateRangeModalProps {
  onSave: (startDate: Moment | null, endDate: Moment | null) => void;
}

interface SavedDatesRefType {
  startDate: Moment | null;
  endDate: Moment | null;
}

export default function DateRangeModal({ onSave }: DateRangeModalProps) {
  const { formatMessage } = useIntl();

  const [saved, setSaved] = useState(true);
  const [startDate, setStartDate] = useState<Moment | null>(
    moment().startOf('month'),
  );
  const [endDate, setEndDate] = useState<Moment | null>(
    moment().endOf('month'),
  );
  const savedDatesRef = useRef<SavedDatesRefType>({
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
  });

  const rangeSelected = useMemo(() => {
    return startDate && endDate;
  }, [startDate, endDate]);

  const buttonText = useMemo(() => {
    if (!saved) {
      const savedStart = savedDatesRef.current.startDate;
      const savedEnd = savedDatesRef.current.endDate;
      if (!savedStart || !savedEnd) {
        return formatMessage(messages.dateRangeButton);
      }
      return `${forceDateString(savedStart)} - ${forceDateString(savedEnd)}`;
    }
    if (!startDate || !endDate) {
      return formatMessage(messages.dateRangeButton);
    }
    return `${forceDateString(startDate)} - ${forceDateString(endDate)}`;
  }, [saved, startDate, endDate, formatMessage]);

  const handleStartDateChange = useCallback(
    (date: Moment | Moment[] | null) => {
      if (Array.isArray(date)) return Promise.resolve();
      setSaved(false);
      setStartDate(date);
      return Promise.resolve();
    },
    [endDate],
  );

  const handleEndDateChange = useCallback(
    (date: Moment | Moment[] | null) => {
      if (Array.isArray(date)) return Promise.resolve();
      setSaved(false);
      setEndDate(date);
      return Promise.resolve();
    },
    [startDate],
  );

  const handleOpen = useCallback(() => {
    setStartDate(savedDatesRef.current.startDate);
    setEndDate(savedDatesRef.current.endDate);
    setSaved(true);
  }, []);

  const handleSave = useCallback(() => {
    onSave(startDate, endDate);
    setSaved(true);
    savedDatesRef.current = { startDate, endDate };
  }, [startDate, endDate, onSave]);

  return (
    <ConfirmModal
      onOpen={handleOpen}
      onConfirm={handleSave}
      disabled={!rangeSelected}
      title={formatMessage(messages.title)}
      submitTextButton={formatMessage(messages.saveButton)}
      triggerButton={(handleOpen) => (
        <Button
          variant="outlined"
          onClick={handleOpen}
          startIcon={<CalendarMonthOutlinedIcon />}
        >
          {buttonText}
        </Button>
      )}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DatePickerField
            allowPicker
            allowTyping
            value={startDate}
            onChange={handleStartDateChange}
            onBlurChange={handleStartDateChange}
            label={formatMessage(messages.fromDateRangeLabel)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DatePickerField
            allowPicker
            allowTyping
            value={endDate}
            onChange={handleEndDateChange}
            onBlurChange={handleEndDateChange}
            label={formatMessage(messages.toDateRangeLabel)}
          />
        </Grid>
      </Grid>
    </ConfirmModal>
  );
}
